import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { Action, Category } from "constants/events";
import { Deal } from "custom-types/Deals";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import useGetDealKindLabel from "hooks/useGetDealKindLabel";
import { getUserId } from "redux/selectors/user";
import { dealCardUpdateMedRec } from "utils/dealCardUpdateMedRec";
import { getDispensaryUrl } from "utils/dispensaryUrls";

import Carousel from "components/botanic/Carousel";
import DealCard from "components/botanic/DealCard";

const DealsResults: React.FC<{ deals: Deal[]; disableLazyLoad?: boolean }> = ({
  disableLazyLoad = false,
  deals,
}) => {
  const countryCode = useDomainCountryCode();
  const isCanada = countryCode === "CA";
  const userId = useSelector(getUserId);
  const dispatch = useDispatch();
  const { publishEvent } = useEventTracker();
  const { getDealKindLabel } = useGetDealKindLabel();

  const trackFeaturedDealClick = React.useCallback(
    (deal: Deal) => {
      const {
        dispensary: { id: dispensaryId },
      } = deal;

      publishEvent({
        action: Action.click,
        category: Category.featuredDealCards,
        dealId: deal.id,
        dispensaryId,
        label: getDealKindLabel(deal),
        userId: userId as number,
      });
    },
    [userId]
  );

  const trackFeaturedDealImpression = React.useCallback(
    (deal: Deal) => {
      const {
        dispensary: { id: dispensaryId },
      } = deal;

      publishEvent({
        action: Action.impression,
        category: Category.featuredDealCards,
        dealId: deal.id,
        dispensaryId,
        label: getDealKindLabel(deal),
        userId: userId as number,
      });
    },
    [userId]
  );

  if (!deals.length) {
    return null;
  }

  // This is gross because it is a temporary solution for this POC for sponsored Deals
  // https://leafly.atlassian.net/browse/DIGS-3001
  let dealLookup: number[] = [];
  const sponsoredDeals = [...deals].filter((deal) => {
    if (dealLookup.length < 4) {
      if (!dealLookup.includes(deal.dispensary.id)) {
        dealLookup = [...dealLookup, deal.dispensary.id];
        return deal;
      }
    }
    return false;
  });

  const cardClick = async (deal: Deal) => {
    // GA event tracking
    trackFeaturedDealClick(deal);
    // Med/rec deals will have medical and/or recreational properties, check if we need to update the user preference
    if (deal.isMedical || deal.isRecreational) {
      await dealCardUpdateMedRec(deal, dispatch);
    }
  };

  return (
    <section className="py-xxl">
      <div className="container">
        <div className="row">
          <Carousel
            className="col mb-md"
            title={
              isCanada
                ? "Best weed deals near you"
                : "Find dispensary deals near you"
            }
          >
            {sponsoredDeals.map((deal) => {
              const {
                title,
                dispensary: { slug },
              } = deal;

              const isDealCBD = !deal.isMedical && !deal.isRecreational;
              const dealType =
                deal.isMedical && !deal.isRecreational ? "Med" : "Rec";

              const dealUrl = getDispensaryUrl({
                countryCode,
                query: {
                  deal_title: [title],
                  ...(!isDealCBD && { menu_type: dealType }),
                },
                slug,
                tab: "menu",
              });

              return (
                <DealCard
                  data-testid="deal-card"
                  disableLazyLoad={disableLazyLoad}
                  key={deal.id}
                  deal={{ ...deal, dealUrl }}
                  showFulfillment={true}
                  countryCode={countryCode}
                  className="rounded h-full overflow-hidden elevation-low"
                  onClick={() => cardClick(deal)}
                  onLoad={() => trackFeaturedDealImpression(deal)}
                />
              );
            })}
          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default DealsResults;
